import React, { useRef, useState } from 'react';
import {
  View, TouchableOpacity, GestureResponderEvent,
} from 'react-native';
import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import {
  BodyText, PortalOverlay,
} from '..';
import { CloseIcon } from '../Icons';

export type ToolTipProps = {
  message: React.ReactNode,
  closeOthers?: boolean,
  testID: string,
  offsetX?: number,
  offsetY?: number,
  maxWidth?: number,
  onOpen?: () => void,
  onClose?: () => void,
};

/** ToolTip
 * Barebones implementation of a pressable tooltip to get EV-3278 over the
 * finish line. Requires more work to make this more versatile
 * for general usage with things such as edge detection, positioning, and additional
 * customizations
 */
const ToolTip: React.FC<ToolTipProps> = ({
  closeOthers = true,
  offsetX = -10,
  offsetY = 0,
  maxWidth = 250,
  children,
  message,
  testID,
  onOpen,
  onClose,
}) => {
  const viewRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [top, setTop] = useState(0);
  const [left, setLeft] = useState(0);

  const handlePress = (e: GestureResponderEvent) => {
    if (!viewRef.current) return;
    e.stopPropagation();
    viewRef.current.measureInWindow((x, y, width, height) => {
      setTop(y + height + offsetY);
      setLeft(x + width + offsetX);
      setVisible(true);
    });

    if (onOpen && typeof onOpen === 'function') {
      onOpen();
    }
  };

  const handleClose = () => {
    setVisible(false);
    if (onClose && typeof onClose === 'function') {
      onClose();
    }
  };

  return (
    <React.Fragment>
      <TouchableOpacity onPress={handlePress} testID={`${testID}-activator`}>
        <View ref={viewRef}>
          {children}
        </View>
      </TouchableOpacity>
      {visible && (
        <PortalOverlay
          name={`${testID}-overlay`}
          onBackdropClick={handleClose}
          isTransparent
          closeOthers={closeOthers}
        >
          <Box
            testID={testID}
            color={colors.whiteWash}
            borderRadius={4}
            borderColor={colors.lightGrey}
            borderWidth={1}
            borderStyle="solid"
            position="absolute"
            padding={16}
            maxWidth={maxWidth}
            left={left}
            top={top}
            style={[{
              shadowOffset: {
                width: 0,
                height: 8,
              },
              shadowOpacity: 0.25,
              shadowRadius: 16,
            }]}
          >
            <Inline
              alignItems="flex-start"
              justifyContent="space-between"
              flex="1 1 auto"
              space={12}
            >
              <BodyText
                appearance="small"
                color={colors.metal}
                testID={`${testID}-message`}
              >
                {message}
              </BodyText>
              <TouchableOpacity onPress={handleClose} testID={`${testID}-close`}>
                <Box>
                  <CloseIcon width={8} height={8} color={colors.metal} />
                </Box>
              </TouchableOpacity>
            </Inline>
          </Box>
        </PortalOverlay>
      )}
    </React.Fragment>
  );
};

export default ToolTip;
